import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InfiniteScrollSentinel } from '../ui/InfiniteScrollSentinel'
import { useDimensions } from '@/hooks'
import type { ContentItem } from '@/types/Content.ts'
import './windowed-content-grid.css'

// Row heights for calculating visible range
const ROW_HEIGHTS = {
	grid: 296, // Card height (~280px) + gap (16px)
	list: 92,  // List item height (~80px) + gap (12px)
} as const

// Number of extra rows to render above/below viewport for smooth scrolling
const DEFAULT_OVERSCAN = 2

interface WindowedContentGridProps {
	items: ContentItem[]
	viewMode: 'grid' | 'list'
	renderCard: (item: ContentItem, localIndex: number) => ReactNode
	fetchNextPage: () => void
	hasNextPage: boolean
	isFetchingNextPage: boolean
	overscan?: number
}

/**
 * Calculate number of columns based on container width.
 * These breakpoints match the CSS in content-manager-page.css
 */
function getColumnCount(width: number, viewMode: 'grid' | 'list'): number {
	if (viewMode === 'list') return 1
	if (width >= 1280) return 4
	if (width >= 1024) return 3
	if (width >= 768) return 2
	return 1
}

/**
 * WindowedContentGrid - Renders a windowed grid/list of content items
 *
 * Uses padding spacers to create a virtual scrolling effect while preserving
 * the native CSS grid layout. Only renders visible items plus an overscan buffer.
 *
 * Key benefits:
 * - Memory efficient: Only renders visible items
 * - Layout preserved: Uses existing CSS grid classes
 * - Animation friendly: Local index for stagger animations
 * - No external dependencies
 */
export function WindowedContentGrid({
										items,
										viewMode,
										renderCard,
										fetchNextPage,
										hasNextPage,
										isFetchingNextPage,
										overscan = DEFAULT_OVERSCAN,
									}: WindowedContentGridProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [scrollTop, setScrollTop] = useState(0)
	const { width: containerWidth, height: containerHeight } = useDimensions(containerRef, {
		initial: { width: 1280, height: 600 },
	})

	// Calculate column count based on container width
	const columnCount = useMemo(() => {
		return getColumnCount(containerWidth, viewMode)
	}, [containerWidth, viewMode])

	// Calculate row dimensions
	const rowHeight = ROW_HEIGHTS[viewMode]
	const rowCount = Math.ceil(items.length / columnCount)

	// Calculate visible row range with overscan
	const { visibleItems, paddingTop, paddingBottom } = useMemo(() => {
		const startRow = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
		const endRow = Math.min(rowCount, Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan)

		const startIndex = startRow * columnCount
		const endIndex = Math.min(endRow * columnCount, items.length)

		return {
			visibleItems: items.slice(startIndex, endIndex),
			paddingTop: startRow * rowHeight,
			paddingBottom: Math.max(0, (rowCount - endRow) * rowHeight),
		}
	}, [scrollTop, containerHeight, rowHeight, rowCount, columnCount, items, overscan])

	// Handle scroll events
	const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
		setScrollTop(e.currentTarget.scrollTop)
	}, [])

	// Reset scroll position when viewMode changes
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = 0
			setScrollTop(0)
		}
	}, [viewMode])

	return (
		<div
			ref={containerRef}
			className="windowed-content-grid"
			onScroll={handleScroll}
		>
			{/* Spacer div with padding to maintain scroll height */}
			<div
				className="windowed-content-grid-spacer"
				style={{ paddingTop, paddingBottom }}
			>
				{/* Content grid - uses existing CSS classes */}
				<div className={viewMode === 'grid' ? 'playbook-grid' : 'playbook-list'}>
					{visibleItems.map((item, localIndex) => renderCard(item, localIndex))}
				</div>
			</div>

			{/* Infinite scroll sentinel at the bottom */}
			<InfiniteScrollSentinel
				onLoadMore={fetchNextPage}
				hasMore={hasNextPage}
				isLoading={isFetchingNextPage}
			/>
		</div>
	)
}
